import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Modal, KeenIcon, ModalContent, ModalHeader } from '@/components';
import { AuthContext } from "@/auth/providers/JWTProvider.tsx"; // Adjust path as needed

const SessionTimer: React.FC = () => {
    const authContext = useContext(AuthContext);
    const logout = authContext?.logout;

    if (!logout) {
        console.error("Logout function is not available from AuthContext.");
        return null;
    }

    const envTime = parseInt(import.meta.env.VITE_SESSION_TIME || '900', 10);
    const INITIAL_TIME = !isNaN(envTime) && envTime > 0 ? envTime : 900;
    const WARNING_TIME = 60; // 1 minute in seconds

    const [secondsLeft, setSecondsLeft] = useState<number>(INITIAL_TIME);
    const [showModal, setShowModal] = useState<boolean>(false);

    const resetTimer = useCallback(() => {
        setSecondsLeft(INITIAL_TIME);
    }, [INITIAL_TIME]);

    // Decrease timer every second
    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsLeft(prev => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Show modal once when under WARNING_TIME
    useEffect(() => {
        if (secondsLeft <= WARNING_TIME && !showModal && secondsLeft > 0) {
            setShowModal(true);
        }
    }, [secondsLeft, showModal]);

    // Logout when timer runs out
    useEffect(() => {
        if (secondsLeft <= 0) {
            logout();
        }
    }, [secondsLeft, logout]);

    // Reset timer on user activity
    useEffect(() => {
        const events = ['click', 'keypress', 'scroll'];
        events.forEach(event => window.addEventListener(event, resetTimer));
        return () => {
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [resetTimer]);

    const formatTime = (secs: number) => {
        const minutes = Math.floor(Math.max(secs, 0) / 60);
        const seconds = Math.max(secs, 0) % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <>
            <div className="flex items-center gap-2">
                <div
                    className="text-xs cursor-pointer"
                    onClick={() => setShowModal(true)}
                >
                    Auto Logout in {Math.ceil(secondsLeft / 60)} min.
                </div>
            </div>

            <Modal
                open={showModal}
                onClose={() => setShowModal(false)}
            >
                <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
                    <ModalHeader className="p-0 border-0">
                        <div className="flex items-center justify-between flex-wrap grow gap-5">
                            <div className="flex items-baseline space-x-2">
                                <KeenIcon
                                    icon="timer"
                                    className="text-3xl"
                                />
                                <h1 className="text-xl font-semibold leading-none text-gray-900">
                                    Session Timeout Warning
                                </h1>
                            </div>
                        </div>
                    </ModalHeader>
                    <div className="py-4 text-gray-700 text-sm">
                        <p>
                            Your session will expire in {formatTime(secondsLeft)}.<br />
                            For security reasons, you will be automatically logged out when time runs out. Unsaved data will be lost.
                            Do you want to continue your session?
                        </p>
                        <div className="flex mt-5 gap-2">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                    setShowModal(false);
                                    resetTimer();
                                }}
                            >
                                Continue Session
                            </button>
                            <button
                                className="btn btn-sm"
                                onClick={() => {
                                    setShowModal(false);
                                    logout();
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </ModalContent>
            </Modal>
        </>
    );
};

export { SessionTimer };
